import * as React from 'react';
import ReactSelect, {
	DropdownIndicatorProps,
	MultiValue,
	SingleValue,
	components,
} from 'react-select';
import { useFiltersContext } from '../../contexts/filters';
import { ITaxonomy } from '../../types';

interface IProps {
	taxonomy: ITaxonomy;
}

interface ISelectOption {
	label: string;
	value: string;
}

const Pills = ({ taxonomy }: IProps) => {
	let options = taxonomy.terms.map((term) => {
		// decode htmlentities.
		const label = document.createElement('textarea');
		label.innerHTML = term.name;

		return {
			label: label.value,
			value: '' + term.term_id,
		};
	});

	options = [{ label: 'All', value: '' }, ...options];

	const { selectedTax, selectTaxTerm, initSelectedTax } = useFiltersContext();
	const [value, setValue] = React.useState<ISelectOption>(options[0]);
	const prevValue = React.useRef<string>(options[0].value);

	const handleOnChange = (newValue: SingleValue<ISelectOption> | MultiValue<ISelectOption>) => {
		const option = newValue as SingleValue<ISelectOption>;
		if (option && prevValue.current !== option.value && selectTaxTerm) {
			setValue(option);
			selectTaxTerm(taxonomy.name, +option.value);

			if (window.dataLayer) {
				window.dataLayer.push({
					event: 'post_filter',
					filter: taxonomy.name,
					filter_selection: option.label, // eslint-disable-line camelcase
				});
			}

			prevValue.current = option.value;
		}
	};

	const handleCheckBoxChange = (option: ISelectOption) => {
		handleOnChange(option);
	};

	React.useEffect(() => {
		// When filters are reset, set our value back to the first option.
		if (
			!Object.values(selectedTax).length ||
			(Object.values(initSelectedTax).length > 0 && initSelectedTax === selectedTax)
		) {
			setValue(options[0]);
			prevValue.current = options[0].value;
		}
	}, [selectedTax]); // eslint-disable-line react-hooks/exhaustive-deps

	React.useEffect(() => {
		if (initSelectedTax && Object.values(initSelectedTax).length > 0) {
			if (initSelectedTax && taxonomy && taxonomy.name) {
				const initValue = options.find(
					(option) => option.value === String(initSelectedTax[taxonomy.name])
				);

				if (initValue) {
					setValue(initValue);
				}
			}
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const DropdownIndicator = (props: DropdownIndicatorProps<ISelectOption>) => {
		return (
			<components.DropdownIndicator {...props}>
				<svg
					width="20"
					height="20"
					viewBox="0 0 24 24"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						fillRule="evenodd"
						clipRule="evenodd"
						d="M12.0001 14.95L3.81862 6.63867L2.39331 8.04171L12.0001 17.801L21.6068 8.04171L20.1815 6.63867L12.0001 14.95Z"
					/>
				</svg>
			</components.DropdownIndicator>
		);
	};

	return (
		<>
			<ReactSelect
				className="pfa-filter-select pfa-filter-select--mobile"
				classNamePrefix="pfa-filter-select"
				options={options}
				components={{ DropdownIndicator }}
				onChange={handleOnChange}
				value={value}
				key={taxonomy.name}
				isClearable={false}
				isSearchable={false}
				isMulti={false}
			/>
			<div className="pfa-filter-large-pills pfa-filter-large-pills--desktop">
				{options.map((option) => (
					<label
						key={option.value}
						className="pfa-filter-large-pills__label btn"
						htmlFor={`pfa-filter-large-pills-${taxonomy.name}-${option.value}`}
					>
						<input
							type="checkbox"
							id={`pfa-filter-large-pills-${taxonomy.name}-${option.value}`}
							value={option.value}
							className="pfa-filter-large-pills__input"
							checked={value.value === option.value}
							onChange={() => handleCheckBoxChange(option)}
						/>
						{option.label}
					</label>
				))}
			</div>
		</>
	);
};

Pills.displayName = 'Pills';

export default Pills;
